import React, { useRef, useState, useEffect } from 'react';
import { createPcmStreamer } from '../utils/pcmStreamer';

const PushToTalkButton = ({ onTranscriptComplete, disabled, apiUrl }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [partialText, setPartialText] = useState('');
    const wsRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const pcmStreamerRef = useRef(null);
    const accumulatedTextRef = useRef('');

    useEffect(() => {
        return () => {
            cleanup();
        };
    }, []);

    const cleanup = () => {
        if (pcmStreamerRef.current) {
            pcmStreamerRef.current.stop();
            pcmStreamerRef.current = null;
        }
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
            mediaStreamRef.current = null;
        }
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
        setIsRecording(false);
        setPartialText('');
    };

    const handleStartRecording = async (e) => {
        if (e) e.preventDefault();
        if (disabled || isRecording) return;

        try {
            accumulatedTextRef.current = '';
            setPartialText('');

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaStreamRef.current = stream;

            // Determine WebSocket URL
            const wsUrlBase = apiUrl.replace(/^http/, 'ws');
            const ws = new WebSocket(`${wsUrlBase}/api/stt/ws/transcribe`);
            wsRef.current = ws;

            ws.onopen = () => {
                const streamer = createPcmStreamer(stream, (pcmBuffer) => {
                    if (ws.readyState === WebSocket.OPEN) {
                        ws.send(pcmBuffer);
                    }
                });
                pcmStreamerRef.current = streamer;
                setIsRecording(true);
            };

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === 'final') {
                        accumulatedTextRef.current = (accumulatedTextRef.current ? accumulatedTextRef.current + ' ' : '') + data.text;
                        setPartialText(accumulatedTextRef.current);
                    } else if (data.type === 'partial') {
                        // Show partial + accumulated
                        const currentTotal = (accumulatedTextRef.current ? accumulatedTextRef.current + ' ' : '') + data.text;
                        setPartialText(currentTotal);
                    } else if (data.type === 'error') {
                        console.error("STT Error:", data.message);
                        cleanup();
                    }
                } catch (err) {
                    console.error("Error parsing STT message", err);
                }
            };

            ws.onclose = () => {
                if (pcmStreamerRef.current || mediaStreamRef.current) {
                    cleanup();
                }
            };

            ws.onerror = (err) => {
                console.error("STT WebSocket error", err);
                cleanup();
            };

        } catch (err) {
            console.error("Failed to start recording", err);
            alert("Could not access microphone.");
            cleanup();
        }
    };

    const handleStopRecording = (e) => {
        if (e) e.preventDefault();
        if (!isRecording) return;

        // Stop PCM streaming immediately
        if (pcmStreamerRef.current) {
            pcmStreamerRef.current.stop();
            pcmStreamerRef.current = null;
        }

        // Send stop signal
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send("stop");

            // Wait a brief moment or handle final closure? 
            // Logic says "On ws.close: call onTranscriptComplete"
            // We'll hook into onclose or just wait for it.
            // Let's modify onclose to differentiate between "button release" and "error close"

            // For this implementation, we can just wait for the server to close it, 
            // OR strictly speaking we should probably trigger completion on "stop" sent if we trust the buffer is drained.
            // But usually we wait for final transcript. 
            // The existing `StudentSession` logic cleared `wsRef` on close.

            // Let's rely on `ws.onclose` to trigger the callback

            // Override onclose to handle the completion

            wsRef.current.onclose = () => {
                const finalText = (accumulatedTextRef.current || '').trim();
                if (finalText.length > 0) {
                    onTranscriptComplete(finalText);
                }
                cleanup();
            };
        } else {
            console.warn("WebSocket not open when stopping");
            cleanup(); // Force cleanup
        }
    };

    return (
        <div style={{ position: 'relative', display: 'inline-block' }}>
            {/* Permission / Status Overlay could go here */}
            {isRecording && partialText && (
                <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    marginBottom: '10px',
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    color: 'white',
                    padding: '5px 10px',
                    borderRadius: '4px',
                    whiteSpace: 'nowrap',
                    fontSize: '12px',
                    pointerEvents: 'none',
                    zIndex: 10
                }}>
                    {partialText.length > 30 ? '...' + partialText.slice(-30) : partialText}
                </div>
            )}

            <button
                onMouseDown={handleStartRecording}
                onMouseUp={handleStopRecording}
                onMouseLeave={handleStopRecording}
                onTouchStart={handleStartRecording}
                onTouchEnd={handleStopRecording}
                type="button"
                disabled={disabled}
                style={{
                    padding: '12px 24px',
                    background: isRecording ? '#ff4d4f' : '#0070f3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '24px', // pill shape
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: isRecording ? '0 0 10px rgba(255, 77, 79, 0.5)' : 'none',
                    transition: 'all 0.2s ease',
                    userSelect: 'none', // prevent text selection while holding
                    WebkitUserSelect: 'none',
                    touchAction: 'none' // prevent scrolling while holding on mobile
                }}
            >
                {isRecording ? (
                    <>
                        <span style={{
                            display: 'inline-block',
                            width: '8px',
                            height: '8px',
                            backgroundColor: 'white',
                            borderRadius: '50%',
                            marginRight: '8px',
                            animation: 'pulse 1s infinite'
                        }} />
                        Recording...
                    </>
                ) : (
                    <>
                        Hold to Speak
                    </>
                )}
            </button>
            <style>{`
                @keyframes pulse {
                    0% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.2); opacity: 0.8; }
                    100% { transform: scale(1); opacity: 1; }
                }
            `}</style>
        </div>
    );
};

export default PushToTalkButton;
